import RichText from '@eneco/components/RichText/RichText';
import { Box, Card, Stack, Image, Heading, Text, Expandable, TextLink, useMediaQuery } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { SmartTargetingBannerRenderingExtended } from '@sitecore/types/manual/be/SmartTargetingBannerExtended';

type TextColor = 'textPrimary' | 'textInverted';

type ImageBlockProps = {
  src?: string;
  alt?: string;
  width: string;
};

const ImageBlock = ({ src, alt, width }: ImageBlockProps) => {
  if (!src) return null;

  return <Image src={src} alt={alt ?? ''} width={width} objectFit="contain" borderRadius="rounded" />;
};

type ContentBlockProps = {
  content: string;
  link: {
    href: string;
    target?: string;
    text?: string;
  };
  textColor: TextColor;
};

const ContentBlock = ({ content, link, textColor }: ContentBlockProps) => (
  <Stack gap="4">
    <Text size="BodyM" color={textColor}>
      <RichText html={content} />
    </Text>
    {link.text && (
      <Text size="BodyM" color={textColor}>
        <TextLink emphasis="low" href={link?.href} target={link?.target}>
          {link?.text}
        </TextLink>
      </Text>
    )}
  </Stack>
);

const SmartTargetingBanner = () => {
  const { fields } = useContent<SmartTargetingBannerRenderingExtended>();
  const isDesktop = useMediaQuery('lg');

  const hasImage = fields.data?.image?.value?.formats?.length > 0;
  const image = fields.data?.image?.value?.formats?.[0]?.src;

  const backgroundColorHex =
    fields.items?.find(x => x.name === fields.data?.backgroundColorOption?.value)?.fields.data?.colorCodeText?.value ||
    '';

  const textColor: TextColor =
    (fields?.items?.find(x => x.name === fields?.data?.backgroundColorOption?.value)?.fields?.data?.textColorOption
      ?.value as TextColor) || 'textPrimary';

  return (
    <Card>
      {/* eslint-disable-next-line dxp-rules/no-inline-css */}
      <div style={{ backgroundColor: backgroundColorHex, borderRadius: '8px' }}>
        <Box paddingX="6" paddingY="6">
          {isDesktop ? (
            <Stack gap="4" direction="row" alignY="start">
              {hasImage && <ImageBlock src={image} alt={fields.data?.image?.value?.alt} width="168px" />}
              <Stack.Item>
                <Stack direction="column" alignY="start">
                  <Heading size="2XS" as="h1" color={textColor}>
                    {fields.data?.title?.value}
                  </Heading>
                  <ContentBlock
                    content={fields.data?.content?.value}
                    link={fields.data?.link?.value}
                    textColor={textColor}
                  />
                </Stack>
              </Stack.Item>
            </Stack>
          ) : (
            <Expandable>
              <Expandable.Trigger label="content">
                <Heading size="2XS" as="h1" color={textColor}>
                  {fields.data?.title?.value}
                </Heading>
              </Expandable.Trigger>
              <Expandable.Content>
                <Stack gap="4">
                  <ContentBlock
                    content={fields.data?.content?.value}
                    link={fields.data?.link?.value}
                    textColor={textColor}
                  />
                  <ImageBlock src={image} alt={fields.data?.image?.value?.alt} width="100%" />
                </Stack>
              </Expandable.Content>
            </Expandable>
          )}
        </Box>
      </div>
    </Card>
  );
};

export default SmartTargetingBanner;
